import { Button } from "@mui/material"
import { ChangeEvent, useEffect, useRef, useState } from "react"
import { toast } from "react-toastify"
import CameraIcon from "../assets/icons/camera-icon"
import CrossIcon from "../assets/icons/cross-icon"
import DeleteIcon from "../assets/icons/delete-icon"
import FilesIcon from "../assets/icons/files-icon"
import GalleryIcon from "../assets/icons/gallery-icon"
import InfoIcon from "../assets/icons/info-icon"
import {
    finishWebView,
    pickImageFromCamera,
    pickImageFromGallery,
} from "../shared/app-interface"
import {
    APP_INTERACTION_TYPES,
    DOCUMENT_ASSESSMENT_STAGE,
    FILE_UPLOAD_SOURCE,
} from "../shared/constants"
import { EVENTS } from "../shared/constants/MIXAPANEL_EVENTS"
import { useAssessmentStore } from "../shared/store"
import { uploadDocument } from "../shared/utils/api-client"
import { CompletionObjectType } from "../shared/utils/assessment-types"
import { getAssessmentTrackersData } from "../shared/utils/get-assessment-trackers"
import { trackMixpanelEvent } from "../utils/helpers"
import BottomSheet from "./bottom-sheet"
import Dialog from "./dialog"
import Shell from "./shell"

interface AssessmentProps {
    handleOnCompletion: (completionObject: CompletionObjectType) => void
    handleSkip: () => void
    webSuccessFlow: boolean
}

const Assessment = ({
    handleOnCompletion,
    handleSkip,
    webSuccessFlow,
}: AssessmentProps) => {
    const { assesmentQuestions, setOpenExitFlow, setOpenSuccessFlow, currentStep } =
        useAssessmentStore((state) => state)
    const isVasFlow = assesmentQuestions?.mandatory || false
    const entity = assesmentQuestions?.questions[currentStep]?.entity_type
    const [fileSelected, setFileSelected] = useState(false)
    const [filePreview, setFilePreview] = useState("")
    const fileBlogRef = useRef<Blob>()
    const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false)
    const [showRemovePopup, setShowRemovePopup] = useState(false)
    const [showExitPopup, setShowExitPopup] = useState(false)
    const fileRef = useRef<HTMLInputElement>(null)
    const questionId = assesmentQuestions?.questions[currentStep]?.questions[0]?.id
    const questionType = assesmentQuestions?.questions[currentStep]?.question_type
    const handleUploadClick = () => {
        // call accessing the upload functionality
        //after the upload call this to update the store
        // updateAssessmentQuestions()
        setIsBottomSheetOpen(true)
        getAssessmentTrackersData(
            entity,
            DOCUMENT_ASSESSMENT_STAGE.UPLOAD_CTA_CLICK,
            isVasFlow
        )
        // handleOnCompletion()
    }

    useEffect(() => {
        if (assesmentQuestions?.questions?.length > 0) {
            if (entity) {
                trackMixpanelEvent(EVENTS.ENRICHMENT_SCREEN_SHOWN, {
                    Type: isVasFlow ? "VAS" : "Application flow enrichment",
                    "Entity Shown": entity,
                })
            }
        }
    }, [assesmentQuestions, entity, isVasFlow])

    const handleImageUploadBottomSheetClose = () => {
        const isOnlyImageUploadPopupOpen =
            useAssessmentStore.getState().popupStacks.length === 1
        const openSuccessFlow = useAssessmentStore.getState().openSuccessFlow

        if (isOnlyImageUploadPopupOpen && filePreview && !openSuccessFlow) {
            setShowExitPopup(true)
        } else {
            setIsBottomSheetOpen(false)
        }
    }

    const handleRemoveClick = () => {
        setShowRemovePopup(true)
    }

    const handleRemoveImage = () => {
        getAssessmentTrackersData(
            entity,
            DOCUMENT_ASSESSMENT_STAGE.DOCUMENT_REMOVED_CLICK,
            isVasFlow
        )
        setFilePreview("")
        setFileSelected(false)
        setShowRemovePopup(false)
    }
    const handleImageSubmit = async () => {
        const completionObject = {
            question_id: questionId,
            question_type: questionType,
        }
        if (isVasFlow) {
            trackMixpanelEvent(EVENTS.ASSESSMENT_PARTIAL_EVALUATION, {
                "Section Name": "VAS DL",
                "Section Result": "Pending",
            })
        }
        getAssessmentTrackersData(
            entity,
            DOCUMENT_ASSESSMENT_STAGE.DOCUMENT_UPLOAD_INITIATED,
            isVasFlow
        )

        const uploadURL =
            assesmentQuestions?.questions?.[currentStep]?.questions?.[0]?.upload_data
                ?.upload_urls?.[0]
        const uploadSignedHeaders =
            assesmentQuestions?.questions?.[currentStep]?.questions?.[0]?.upload_data
                ?.signed_headers
        try {
            await uploadDocument(uploadURL, fileBlogRef.current, {
                ...uploadSignedHeaders,
                "content-type": "image/png",
            })
            getAssessmentTrackersData(
                entity,
                DOCUMENT_ASSESSMENT_STAGE.DOCUMENT_UPLOAD_SUCCESS,
                isVasFlow
            )
            setFilePreview("")
            setIsBottomSheetOpen(false)
            handleOnCompletion(completionObject)
        } catch (e) {
            console.log(e)
            getAssessmentTrackersData(
                entity,
                DOCUMENT_ASSESSMENT_STAGE.DOCUMENT_UPLOAD_FAILURE,
                isVasFlow
            )
            toast.error("There was an error uploading image. please try again")
        }
        // TODO: finish call upload API call
    }

    const handleExitWebview = () => {
        const isVasFlow = useAssessmentStore.getState().assesmentQuestions.mandatory
        if (isVasFlow) {
            finishWebView(
                JSON.stringify({
                    intent: "exit",
                    flowType: isVasFlow ? "vas" : "enrichment",
                })
            )
        } else {
            // closing all and opening success flow in case of enrichment flow
            // finishWebflow call here
            if (!webSuccessFlow) {
                const data = {
                    intent: "navigate",
                    flowType: isVasFlow ? "vas" : "enrichment",
                    navigateTo: "screen_details",
                }
                finishWebView(JSON.stringify(data))
            } else {
                setIsBottomSheetOpen(false)
                setOpenExitFlow(false)
                setShowExitPopup(false)
                setOpenSuccessFlow(true)
            }
        }
    }

    const handleImagePickFromGallery = () => {
        getAssessmentTrackersData(
            entity,
            DOCUMENT_ASSESSMENT_STAGE.ADD_DOCUMENT_CLICK,
            isVasFlow,
            FILE_UPLOAD_SOURCE.GALLERY
        )

        pickImageFromGallery(true)
    }

    const handleImagePickFromCamera = () => {
        getAssessmentTrackersData(
            entity,
            DOCUMENT_ASSESSMENT_STAGE.ADD_DOCUMENT_CLICK,
            isVasFlow,
            FILE_UPLOAD_SOURCE.CAMERA
        )
        pickImageFromCamera(true)
    }
    const handleImagePickFromFiles = (e: ChangeEvent<HTMLInputElement>) => {
        getAssessmentTrackersData(
            entity,
            DOCUMENT_ASSESSMENT_STAGE.ADD_DOCUMENT_CLICK,
            isVasFlow,
            FILE_UPLOAD_SOURCE.FILES
        )
        if (e.target.files) {
            setFilePreview(URL.createObjectURL(e.target.files[0]))
            setFileSelected(true)
            fileBlogRef.current = e.target.files[0]
        }
    }

    useEffect(() => {
        trackMixpanelEvent(EVENTS.ASSESSMENT_PAGE_SHOWN, {
            "Section Name": "VAS DL",
            "is VAS Present": true,
        })
        window.addEventListener(APP_INTERACTION_TYPES.CAMERA, (e: any) => {
            console.log(e)
            if (e.detail.success) {
                const base64Url = `${
                    e.detail.imageData.indexOf("base64") > -1
                        ? ""
                        : "data:image/png;base64,"
                }${e.detail.imageData}`

                setFilePreview(base64Url)
                setFileSelected(true)
                fetch(base64Url)
                    .then((res) => res.blob())
                    .then((blob) => {
                        fileBlogRef.current = blob
                    })
            }
        })
    }, [])

    useEffect(() => {
        if (assesmentQuestions?.questions?.length > 0) {
            const state = useAssessmentStore.getState()
            const isVasFlow = state.assesmentQuestions.mandatory
            if (entity) {
                trackMixpanelEvent(EVENTS.ENRICHMENT_SCREEN_SHOWN, {
                    Type: isVasFlow ? "VAS" : "Application flow enrichment",
                    "Entity Shown": entity,
                })
            }
        }
    }, [assesmentQuestions, entity])

    return (
        <Shell
            footerElement={
                <div className="flex flex-col gap-4 items-center justify-center w-full">
                    {isVasFlow && (
                        <span className="text-onSurface-secondary text-xs text-center px-[35px] ">
                            Your Application would not be Successful if you do not
                            complete this step
                        </span>
                    )}

                    <div className="flex w-full flex-col items-center justify-center px-6 mb-10">
                        <Button
                            fullWidth
                            onClick={handleUploadClick}
                            style={{ padding: "16px", borderRadius: "4px" }}
                        >
                            <span>Upload</span>
                        </Button>
                        {!assesmentQuestions?.mandatory && (
                            <Button onClick={handleSkip} variant="text">
                                Skip
                            </Button>
                        )}
                    </div>
                </div>
            }
        >
            <>
                <BottomSheet
                    sheetId="file-upload"
                    PaperProps={{
                        sx: {
                            borderRadius: "16px 16px 0 0",
                        },
                    }}
                    open={isBottomSheetOpen}
                    anchor="bottom"
                    onClose={handleImageUploadBottomSheetClose}
                >
                    <div className="rounded-t-lg">
                        <div className="flex justify-between py-6 px-4 border-b">
                            <div>
                                <h3 className="text-base font-semibold text-onSurface-primary">
                                    Upload your Driving Licence
                                </h3>
                                <p className="text-onSurface-secondary text-sm">
                                    Front side only
                                </p>
                            </div>
                            <button onClick={handleImageUploadBottomSheetClose}>
                                <CrossIcon />
                            </button>
                        </div>
                        <div className="p-4">
                            {!fileSelected && (
                                <>
                                    <img
                                        src="https://cdn.apna.co/cloudinary/vas_assesment/dl-image.png"
                                        alt=""
                                    />
                                    <p className="mt-3 text-sm text-onSurface-secondary mb-4">
                                        Select one of the following options
                                    </p>
                                    <div className="flex space-x-5">
                                        <ActionButton onClick={handleImagePickFromCamera}>
                                            <CameraIcon />
                                            <span>Camera</span>
                                        </ActionButton>
                                        <ActionButton
                                            onClick={handleImagePickFromGallery}
                                        >
                                            <GalleryIcon />
                                            <span>Gallery</span>
                                        </ActionButton>
                                        <ActionButton
                                            onClick={() => fileRef.current?.click()}
                                        >
                                            <FilesIcon />
                                            <input
                                                onChange={(e) =>
                                                    handleImagePickFromFiles(e)
                                                }
                                                ref={fileRef}
                                                className="hidden"
                                                type="file"
                                                accept="image/*"
                                            />
                                            <span>Files</span>
                                        </ActionButton>
                                    </div>
                                </>
                            )}
                            {fileSelected && (
                                <>
                                    <img
                                        src={filePreview}
                                        className="max-h-[300px] w-auto mx-auto"
                                    />
                                    <div className="flex mt-1 bg-[#FFFAED] p-2 rounded text-onSurface-secondary font-semibold text-sm space-x-2 items-center">
                                        <span className="flex-grow">
                                            <InfoIcon />
                                        </span>
                                        <span>
                                            On clicking submit you agree to the{" "}
                                            <a
                                                className="text-secondary
                                        "
                                                href="https://apna.co/user-agreement"
                                            >
                                                Terms of service
                                            </a>{" "}
                                            and{" "}
                                            <a
                                                className="text-secondary"
                                                href="https://apna.co/privacy"
                                            >
                                                privacy policy
                                            </a>{" "}
                                            of apna
                                        </span>
                                    </div>
                                    <div className="flex justify-between mt-4">
                                        <Button
                                            onClick={handleRemoveClick}
                                            startIcon={<DeleteIcon />}
                                            color="error"
                                            variant="outlined"
                                        >
                                            Remove
                                        </Button>
                                        <Button onClick={handleImageSubmit}>
                                            <div className="flex gap-1">
                                                {/* {isUploaded && (
                                                    <CircularProgress
                                                        size={19}
                                                        style={{ color: "white" }}
                                                    />
                                                )} */}
                                                Submit
                                            </div>
                                        </Button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </BottomSheet>
                <Dialog
                    onClose={() => null}
                    dialogId="remove-file"
                    open={showRemovePopup}
                    sx={{
                        "& .MuiPaper-root": {
                            width: "80%",
                        },
                    }}
                >
                    <div className="mx-auto px-4 py-10">
                        <h3
                            className="text-center text-onSurface-primary font-semibold
                    "
                        >
                            Are you sure you want to remove this Driving Licence?
                        </h3>
                        <div className="flex space-between mt-8 space-x-4">
                            <Button
                                onClick={() => setShowRemovePopup(false)}
                                className="w-full"
                                variant="outlined"
                            >
                                Cancel
                            </Button>
                            <Button onClick={handleRemoveImage} className="w-full">
                                Yes
                            </Button>
                        </div>
                    </div>
                </Dialog>
                <Dialog
                    onClose={() => null}
                    dialogId="exit-popup"
                    open={showExitPopup}
                    sx={{
                        "& .MuiPaper-root": {
                            width: "80%",
                        },
                    }}
                >
                    <div className="mx-auto px-4 py-10">
                        <h3
                            className="text-center text-onSurface-primary font-semibold
                    "
                        >
                            Do you want to exit? Your changes will not be saved.
                        </h3>
                        <div className="flex space-between mt-8 space-x-4">
                            <Button
                                onClick={() => setShowExitPopup(false)}
                                className="w-full"
                            >
                                No
                            </Button>
                            <Button
                                onClick={handleExitWebview}
                                className="w-full"
                                variant="outlined"
                            >
                                Yes
                            </Button>
                        </div>
                    </div>
                </Dialog>
            </>
        </Shell>
    )
}

export default Assessment

const ActionButton = ({ ...props }: any) => {
    return (
        <div
            className="flex flex-col space-y-0.5 w-full p-3 border border-secondary
    rounded items-center justify-center text-base font-semibold text-secondary"
            {...props}
        ></div>
    )
}
